import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { isRouteLoading, subscribeRouteLoading } from "@/lib/routeLoading";

// Under this, the chunk lands fast enough that a bar would only flash. Every
// route except Dashboard is <=34 KB and clears it on a normal connection.
const SHOW_AFTER_MS = 120;
// Once shown, hold it so a near-instant finish is not a flicker.
const MIN_VISIBLE_MS = 320;

/**
 * Indeterminate progress bar shown while a route chunk downloads.
 *
 * Renders outside the router on purpose: its signal is the dynamic import, not
 * the location, so it commits as an urgent update even while React is holding
 * the suspended route transition uncommitted.
 */
export default function RouteProgress() {
  const loading = useSyncExternalStore(subscribeRouteLoading, isRouteLoading, () => false);
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);
  const shownAt = useRef(0);

  useEffect(() => {
    if (loading) {
      const showTimer = window.setTimeout(() => {
        shownAt.current = Date.now();
        visibleRef.current = true;
        setVisible(true);
      }, SHOW_AFTER_MS);
      return () => window.clearTimeout(showTimer);
    }

    if (!visibleRef.current) return;
    const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt.current));
    const hideTimer = window.setTimeout(() => {
      visibleRef.current = false;
      setVisible(false);
    }, remaining);
    return () => window.clearTimeout(hideTimer);
  }, [loading]);

  if (!visible) return null;

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5 overflow-hidden" aria-hidden="true">
        <div className="route-progress-bar h-full w-2/5 rounded-full" />
      </div>
      <span role="status" className="sr-only">
        Loading page
      </span>
    </>
  );
}
